import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  limit,
  arrayUnion,
  arrayRemove,
  writeBatch,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const GroupContext = createContext();

export function useGroups() {
  return useContext(GroupContext);
}

export function GroupProvider({ children }) {
  const { currentUser, refreshUserProfile } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch groups for the current user
  const fetchGroups = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // First, try a simple query to test basic Firestore access
      try {
        const simpleQuery = query(collection(db, 'groups'), limit(1));
        await getDocs(simpleQuery);
      } catch (simpleError) {
        console.warn('GroupContext: Basic Firestore access failed:', simpleError);
        throw new Error(`Firestore access issue: ${simpleError.message}`);
      }
      
      // Query groups where the current user is a member
      const groupsQuery = query(
        collection(db, 'groups'),
        where('members', 'array-contains', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(groupsQuery);
      const groupsData = [];
      
      querySnapshot.forEach((doc) => {
        groupsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('GroupContext: Fetched groups:', groupsData);
      console.log('GroupContext: Current user UID:', currentUser.uid);
      // console.log('GroupContext: Groups where user is member:', groupsData.map(g => ({ id: g.id, name: g.name, members: g.members })));
      
      setGroups(groupsData);
    } catch (error) {
      console.error('GroupContext: Error fetching groups:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to fetch groups. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check Firestore security rules.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Missing Firestore index. Please create the required index.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firestore is currently unavailable. Please try again later.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      
      // Set empty groups array to prevent UI crashes
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Create a new group
  const createGroup = useCallback(async (groupData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to create a group');
    }

    try {
      const newGroup = {
        name: groupData.name.trim(),
        description: groupData.description.trim(),
        privacy: groupData.privacy || 'private',
        members: [currentUser.uid], // Creator is automatically a member
        memberCount: 1,
        memberRoles: { [currentUser.uid]: 'admin' }, // Creator is admin
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        maxMembers: 50 // Default max members
      };

      // Add group to Firestore
      const docRef = await addDoc(collection(db, 'groups'), newGroup);
      
      // Update user profile to include the new group
      const userDocRef = doc(db, 'users', currentUser.uid);
      const currentGroups = currentUser.groups || [];
      await updateDoc(userDocRef, {
        groups: [...currentGroups, docRef.id],
        updatedAt: serverTimestamp()
      });

      // Add the new group to local state
      const createdGroup = {
        id: docRef.id,
        ...newGroup,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setGroups(prev => [createdGroup, ...prev]);
      
      // Refresh user profile to sync the groups array
      await refreshUserProfile();
      
      return createdGroup;
    } catch (error) {
      console.error('GroupContext: Error creating group:', error);
      throw new Error('Failed to create group. Please try again.');
    }
  }, [currentUser, refreshUserProfile]);

  // Update an existing group
  const updateGroup = useCallback(async (groupId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update a group');
    }

    try {
      const groupRef = doc(db, 'groups', groupId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(groupRef, updateData);
      
      // Update local state
      setGroups(prev => prev.map(group => 
        group.id === groupId 
          ? { ...group, ...updates, updatedAt: new Date() }
          : group
      ));
      
    } catch (error) {
      console.error('GroupContext: Error updating group:', error);
      throw new Error('Failed to update group. Please try again.');
    }
  }, [currentUser]);

  // Delete a group
  const deleteGroup = useCallback(async (groupId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to delete a group');
    }

    try {
      // Check if user is the creator of the group
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        throw new Error('Group not found');
      }
      
      if (group.createdBy !== currentUser.uid) {
        throw new Error('Only group creators can delete groups');
      }

      // Delete group from Firestore
      await deleteDoc(doc(db, 'groups', groupId));
      
      // Update user profile to remove the group
      const userDocRef = doc(db, 'users', currentUser.uid);
      const currentGroups = currentUser.groups || [];
      const updatedGroups = currentGroups.filter(id => id !== groupId);
      
      await updateDoc(userDocRef, {
        groups: updatedGroups,
        updatedAt: serverTimestamp()
      });

      // Remove from local state
      setGroups(prev => prev.filter(group => group.id !== groupId));
      
      // Refresh user profile to sync the groups array
      await refreshUserProfile();
      
    } catch (error) {
      console.error('GroupContext: Error deleting group:', error);
      throw new Error('Failed to delete group. Please try again.');
    }
  }, [currentUser, groups, refreshUserProfile]);

  // Add member to group
  const addMemberToGroup = useCallback(async (groupId, userEmail, role = 'member') => {
    if (!currentUser) {
      throw new Error('User must be authenticated to add members');
    }

    try {
      // Check if user has permission to add members
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        throw new Error('Group not found');
      }
      
      if (!isUserAdmin(groupId) && !isUserCreator(groupId)) {
        throw new Error('Only admins can add members to groups');
      }

      // Find user by email
      const usersQuery = query(collection(db, 'users'), where('email', '==', userEmail));
      const userSnapshot = await getDocs(usersQuery);
      
      if (userSnapshot.empty) {
        throw new Error('User not found. They must sign up for the app first.');
      }

      const userDoc = userSnapshot.docs[0];
      const userId = userDoc.id;
      
      // Check if user is already a member
      if (group.members.includes(userId)) {
        throw new Error('User is already a member of this group');
      }

      // Check group capacity
      if (group.memberCount >= (group.maxMembers || 50)) {
        throw new Error('Group has reached maximum capacity');
      }

      // Update group with new member
      const groupRef = doc(db, 'groups', groupId);
      const batch = writeBatch(db);
      
      // Add member to group
      batch.update(groupRef, {
        members: arrayUnion(userId),
        memberCount: group.memberCount + 1,
        [`memberRoles.${userId}`]: role,
        updatedAt: serverTimestamp()
      });

      // Add group to user's profile
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        groups: arrayUnion(groupId),
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      // Update local state
      setGroups(prev => prev.map(g => {
        if (g.id === groupId) {
          return {
            ...g,
            members: [...g.members, userId],
            memberCount: g.memberCount + 1,
            memberRoles: { ...g.memberRoles, [userId]: role },
            updatedAt: new Date()
          };
        }
        return g;
      }));

      return { success: true, userId, role };
    } catch (error) {
      console.error('GroupContext: Error adding member:', error);
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, groups]);

  // Remove member from group
  const removeMemberFromGroup = useCallback(async (groupId, userId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to remove members');
    }

    try {
      // Check if user has permission to remove members
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        throw new Error('Group not found');
      }
      
      if (!isUserAdmin(groupId) && !isUserCreator(groupId)) {
        throw new Error('Only admins can remove members from groups');
      }

      // Prevent removing the group creator
      if (userId === group.createdBy) {
        throw new Error('Cannot remove the group creator');
      }

      // Prevent removing yourself if you're not the creator
      if (userId === currentUser.uid && !isUserCreator(groupId)) {
        throw new Error('Cannot remove yourself from the group');
      }

      // Update group
      const groupRef = doc(db, 'groups', groupId);
      const batch = writeBatch(db);
      
      batch.update(groupRef, {
        members: arrayRemove(userId),
        memberCount: group.memberCount - 1,
        updatedAt: serverTimestamp()
      });

      // Remove group from user's profile
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        groups: arrayRemove(groupId),
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      // Update local state
      setGroups(prev => prev.map(g => {
        if (g.id === groupId) {
          const { [userId]: removedRole, ...remainingRoles } = g.memberRoles || {};
          return {
            ...g,
            members: g.members.filter(id => id !== userId),
            memberCount: g.memberCount - 1,
            memberRoles: remainingRoles,
            updatedAt: new Date()
          };
        }
        return g;
      }));

      return { success: true, userId };
    } catch (error) {
      console.error('GroupContext: Error removing member:', error);
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, groups]);

  // Update member role
  const updateMemberRole = useCallback(async (groupId, userId, newRole) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update member roles');
    }

    try {
      // Check if user has permission to update roles
      if (!isUserAdmin(groupId) && !isUserCreator(groupId)) {
        throw new Error('Only admins can update member roles');
      }

      // Prevent changing the creator's role
      const group = groups.find(g => g.id === groupId);
      if (userId === group.createdBy) {
        throw new Error('Cannot change the group creator\'s role');
      }

      // Update group
      const groupRef = doc(db, 'groups', groupId);
      await updateDoc(groupRef, {
        [`memberRoles.${userId}`]: newRole,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setGroups(prev => prev.map(g => {
        if (g.id === groupId) {
          return {
            ...g,
            memberRoles: { ...g.memberRoles, [userId]: newRole },
            updatedAt: new Date()
          };
        }
        return g;
      }));

      return { success: true, userId, newRole };
    } catch (error) {
      console.error('GroupContext: Error updating member role:', error);
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, groups]);

  // Send invitation to non-member
  const sendInvitation = useCallback(async (groupId, email, message = '') => {
    if (!currentUser) {
      throw new Error('User must be authenticated to send invitations');
    }

    try {
      // Check if user has permission to send invitations
      if (!isUserAdmin(groupId) && !isUserCreator(groupId)) {
        throw new Error('Only admins can send invitations');
      }

      // Check if email is already a member
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        throw new Error('Group not found');
      }

      // Check invitation status and group membership BEFORE creating invitation
      const status = await checkInvitationAndMembershipStatus(groupId, email);
      
      // Check 1: Prevent duplicate pending invitations
      if (status.hasInvitation && status.status === 'pending') {
        const invitedDate = status.invitedAt?.toDate?.() || new Date();
        const formattedDate = invitedDate.toLocaleDateString();
        const errorMsg = `Cannot send invitation: ${email} already has a pending invitation to this group (sent on ${formattedDate}). Please wait for them to respond to the existing invitation.`;
        throw new Error(errorMsg);
      }
      
      // Check 2: Prevent inviting existing members
      if (status.isMember) {
        const errorMsg = `Cannot send invitation: ${email} is already a member of this group.`;
        throw new Error(errorMsg);
      }
      
      // Create invitation in separate collection for better management
      const invitationData = {
        groupId,
        groupName: group.name,
        groupDescription: group.description,
        email,
        inviteeId: status.userId, // Add the invitee's UID for security rules
        message,
        invitedBy: currentUser.uid,
        invitedByEmail: currentUser.email,
        invitedAt: serverTimestamp(),
        status: 'pending'
      };

      console.log('GroupContext: sendInvitation - Creating invitation with data:', invitationData);
      console.log('GroupContext: sendInvitation - Status from checkInvitationAndMembershipStatus:', status);

      let invitationRef;
      try {
        console.log('GroupContext: sendInvitation - About to create invitation document with data:', invitationData);
        invitationRef = await addDoc(collection(db, 'invitations'), invitationData);
        console.log('GroupContext: sendInvitation - Invitation created successfully with ID:', invitationRef.id);
        
        // Verify the invitation was created by reading it back
        const createdInvitation = await getDoc(invitationRef);
        console.log('GroupContext: sendInvitation - Created invitation data:', createdInvitation.data());
      } catch (error) {
        console.error('GroupContext: Failed to create invitation document:', error);
        console.error('GroupContext: Error details:', {
          code: error.code,
          message: error.message,
          details: error.details
        });
        // Fallback: just update the group with invitation info
        invitationRef = { id: `temp_${Date.now()}` };
      }

      // Update group with invitation reference
      try {
        const groupRef = doc(db, 'groups', groupId);
        const pendingInvites = group.pendingInvites || [];
        
        await updateDoc(groupRef, {
          pendingInvites: [...pendingInvites, {
            id: invitationRef.id,
            email,
            message,
            invitedBy: currentUser.uid,
            invitedAt: serverTimestamp(),
            status: 'pending'
          }],
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('GroupContext: Failed to update group with invitation:', error);
        // Continue with local state update even if Firestore update fails
      }

      // Update local state
      setGroups(prev => prev.map(g => {
        if (g.id === groupId) {
          const pendingInvites = g.pendingInvites || [];
          return {
            ...g,
            pendingInvites: [...pendingInvites, {
              id: invitationRef.id,
              email,
              message,
              invitedBy: currentUser.uid,
              invitedAt: serverTimestamp(),
              status: 'pending'
            }],
            updatedAt: new Date()
          };
        }
        return g;
      }));

      return { success: true, email, invitationId: invitationRef.id };
    } catch (error) {
      console.error('GroupContext: Error sending invitation:', error);
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, groups]);

  // Check invitation status and group membership for a user
  const checkInvitationAndMembershipStatus = useCallback(async (groupId, email) => {
    try {
      let invitationStatus = null;
      let membershipStatus = null;
      
      // Check for existing invitations using the group's pendingInvites field
      // This avoids permission issues since we're reading the group document, not the invitations collection
      const groupRef = doc(db, 'groups', groupId);
      const groupDoc = await getDoc(groupRef);
      
      if (groupDoc.exists()) {
        const group = groupDoc.data();
        const pendingInvites = group.pendingInvites || [];
        
        // Check if there's already a pending invitation for this email
        const existingInvitation = pendingInvites.find(invite => invite.email === email);
        
        if (existingInvitation) {
          invitationStatus = {
            hasInvitation: true,
            status: existingInvitation.status || 'pending',
            invitationId: existingInvitation.id,
            invitedAt: existingInvitation.invitedAt
          };
        } else {
          invitationStatus = {
            hasInvitation: false,
            status: null,
            invitationId: null,
            invitedAt: null
          };
        }
        
        // Find user by email in the users collection
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', email),
          limit(1)
        );
        
        console.log('GroupContext: checkInvitationAndMembershipStatus - Looking for user with email:', email);
        
        const userQuerySnapshot = await getDocs(usersQuery);
        console.log('GroupContext: checkInvitationAndMembershipStatus - User query result:', userQuerySnapshot.size, 'documents found');
        
        if (!userQuerySnapshot.empty) {
          const userDoc = userQuerySnapshot.docs[0];
          const userData = userDoc.data();
          
          console.log('GroupContext: checkInvitationAndMembershipStatus - Found user:', userDoc.id, userData.email);
          console.log('GroupContext: checkInvitationAndMembershipStatus - User data keys:', Object.keys(userData));
          
          // Check if user has actually authenticated with the app
          // Users who haven't authenticated won't have auth-related fields
          const isAuthenticated = userData.emailVerified !== undefined || 
                                  userData.lastSignInTime !== undefined ||
                                  userData.createdAt !== undefined;
          
          console.log('GroupContext: checkInvitationAndMembershipStatus - Authentication check:', {
            emailVerified: userData.emailVerified,
            lastSignInTime: userData.lastSignInTime,
            createdAt: userData.createdAt,
            isAuthenticated
          });
          
          if (!isAuthenticated) {
            throw new Error(`Cannot send invitation: ${email} has not authenticated with the app. Please ask them to sign up first.`);
          }
          
          const isMember = group.members.includes(userDoc.id);
          
          membershipStatus = {
            isMember: isMember,
            userId: userDoc.id,
            isAuthenticated: true
          };
          
          console.log('GroupContext: checkInvitationAndMembershipStatus - Membership status:', membershipStatus);
        } else {
          console.log('GroupContext: checkInvitationAndMembershipStatus - No user found with email:', email);
          throw new Error(`Cannot send invitation: ${email} is not registered with the app. Please ask them to sign up first.`);
        }
      } else {
        invitationStatus = {
          hasInvitation: false,
          status: null,
          invitationId: null,
          invitedAt: null
        };
        membershipStatus = {
          isMember: false,
          userId: null,
          isAuthenticated: false
        };
      }
      
      // Combine both statuses
      const finalStatus = {
        ...invitationStatus,
        ...membershipStatus
      };
      
      console.log('GroupContext: checkInvitationAndMembershipStatus - Final status object:', finalStatus);
      
      return finalStatus;
      
    } catch (error) {
      console.error('GroupContext: Error checking invitation and membership status:', error);
      // Re-throw the error so it can be handled properly
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch invitations for the current user
  const fetchUserInvitations = useCallback(async () => {
    if (!currentUser) return [];

    try {
      console.log('GroupContext: fetchUserInvitations - Current user:', currentUser.uid, currentUser.email);
      
      // First, try a simple query without status filter to see if there are any invitations at all
      const simpleQuery = query(
        collection(db, 'invitations'),
        where('inviteeId', '==', currentUser.uid)
      );
      
      console.log('GroupContext: fetchUserInvitations - Simple query created, executing...');
      
      const simpleQuerySnapshot = await getDocs(simpleQuery);
      
      console.log('GroupContext: fetchUserInvitations - Simple query result:', simpleQuerySnapshot.size, 'documents found');
      
      // Now try the full query with status and orderBy
      const invitationsQuery = query(
        collection(db, 'invitations'),
        where('inviteeId', '==', currentUser.uid),
        where('status', '==', 'pending'),
        orderBy('invitedAt', 'desc')
      );
      
      console.log('GroupContext: fetchUserInvitations - Full query created, executing...');
      
      const querySnapshot = await getDocs(invitationsQuery);
      
      console.log('GroupContext: fetchUserInvitations - Full query result:', querySnapshot.size, 'documents found');
      
      const invitations = [];
      
      querySnapshot.forEach((doc) => {
        const invitationData = {
          id: doc.id,
          ...doc.data()
        };
        console.log('GroupContext: fetchUserInvitations - Invitation data:', invitationData);
        invitations.push(invitationData);
      });
      
      console.log('GroupContext: fetchUserInvitations - Returning invitations:', invitations);
      return invitations;
    } catch (error) {
      console.error('GroupContext: Error fetching user invitations:', error);
      console.error('GroupContext: Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      // Return empty array instead of throwing, so UI doesn't crash
      return [];
    }
  }, [currentUser]);

  // Accept an invitation
  const acceptInvitation = useCallback(async (invitationId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to accept invitations');
    }

    try {
      // Get the invitation
      const invitationRef = doc(db, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      const invitation = invitationDoc.data();
      
      // Verify the invitation is for the current user
      if (invitation.inviteeId !== currentUser.uid) {
        throw new Error('This invitation is not for you');
      }
      
      // Verify the invitation is still pending
      if (invitation.status !== 'pending') {
        throw new Error('This invitation has already been processed');
      }
      
      // Get the group
      const groupRef = doc(db, 'groups', invitation.groupId);
      const groupDoc = await getDoc(groupRef);
      
      if (!groupDoc.exists()) {
        throw new Error('Group not found');
      }
      
      const group = groupDoc.data();
      
      // Check if user is already a member
      if (group.members.includes(currentUser.uid)) {
        throw new Error('You are already a member of this group');
      }
      
      // Check group capacity
      if (group.members.length >= (group.maxMembers || 50)) {
        throw new Error('Group is at maximum capacity');
      }
      
      // Use batch write for atomic operations
      const batch = writeBatch(db);
      
      // Update invitation status
      batch.update(invitationRef, {
        status: 'accepted',
        acceptedAt: new Date()
      });
      
      // Add user to group
      batch.update(groupRef, {
        members: arrayUnion(currentUser.uid),
        memberCount: group.members.length + 1,
        [`memberRoles.${currentUser.uid}`]: 'member',
        updatedAt: serverTimestamp()
      });
      
      // Remove invitation from group's pendingInvites
      const updatedPendingInvites = (group.pendingInvites || []).filter(
        invite => invite.id !== invitationId
      );
      batch.update(groupRef, {
        pendingInvites: updatedPendingInvites
      });
      
      // Execute batch
      await batch.commit();
      
      // Refresh groups to get the updated data from Firestore
      await fetchGroups();
      
      return { success: true, groupId: invitation.groupId };
    } catch (error) {
      console.error('GroupContext: Error accepting invitation:', error);
      throw error;
    }
  }, [currentUser, fetchGroups]);

  // Decline an invitation
  const declineInvitation = useCallback(async (invitationId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to decline invitations');
    }

    try {
      // Get the invitation
      const invitationRef = doc(db, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      const invitation = invitationDoc.data();
      
      // Verify the invitation is for the current user
      if (invitation.inviteeId !== currentUser.uid) {
        throw new Error('This invitation is not for you');
      }
      
      // Verify the invitation is still pending
      if (invitation.status !== 'pending') {
        throw new Error('This invitation has already been processed');
      }
      
      // Use batch write for atomic operations
      const batch = writeBatch(db);
      
      // Update invitation status
      batch.update(invitationRef, {
        status: 'declined',
        declinedAt: new Date()
      });
      
      // Remove invitation from group's pendingInvites
      const groupRef = doc(db, 'groups', invitation.groupId);
      const groupDoc = await getDoc(groupRef);
      
      if (groupDoc.exists()) {
        const group = groupDoc.data();
        const updatedPendingInvites = (group.pendingInvites || []).filter(
          invite => invite.id !== invitationId
        );
        batch.update(groupRef, {
          pendingInvites: updatedPendingInvites,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setGroups(prev => prev.map(g => {
          if (g.id === invitation.groupId) {
            const updatedPendingInvites = (g.pendingInvites || []).filter(
              invite => invite.id !== invitationId
            );
            return {
              ...g,
              pendingInvites: updatedPendingInvites,
              updatedAt: new Date()
            };
          }
          return g;
        }));
      }
      
      // Execute batch
      await batch.commit();
      
      return { success: true };
    } catch (error) {
      console.error('GroupContext: Error declining invitation:', error);
      throw error;
    }
  }, [currentUser]);

  // Leave a group
  const leaveGroup = useCallback(async (groupId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to leave a group');
    }

    try {
      // Get the group
      const groupRef = doc(db, 'groups', groupId);
      const groupDoc = await getDoc(groupRef);
      
      if (!groupDoc.exists()) {
        throw new Error('Group not found');
      }
      
      const group = groupDoc.data();
      
      // Check if user is actually a member
      if (!group.members.includes(currentUser.uid)) {
        throw new Error('You are not a member of this group');
      }
      
      // Check if user is the group creator (creators cannot leave, only delete the group)
      if (group.createdBy === currentUser.uid) {
        throw new Error('Group creators cannot leave their own group. You can delete the group instead.');
      }
      
      // Use batch write for atomic operations
      const batch = writeBatch(db);
      
      // Remove user from group
      batch.update(groupRef, {
        members: arrayRemove(currentUser.uid),
        memberCount: group.members.length - 1,
        updatedAt: serverTimestamp()
      });
      
      // Remove user's role from the group
      if (group.memberRoles && group.memberRoles[currentUser.uid]) {
        const updatedMemberRoles = { ...group.memberRoles };
        delete updatedMemberRoles[currentUser.uid];
        batch.update(groupRef, {
          memberRoles: updatedMemberRoles
        });
      }
      
      // Execute batch
      await batch.commit();
      
      // Update local state
      setGroups(prev => prev.filter(g => g.id !== groupId));
      
      return { success: true };
    } catch (error) {
      console.error('GroupContext: Error leaving group:', error);
      throw error;
    }
  }, [currentUser]);

  // Get a specific group by ID
  const getGroupById = useCallback((groupId) => {
    return groups.find(group => group.id === groupId);
  }, [groups]);

  // Check if user is a member of a group
  const isUserMember = useCallback((groupId) => {
    if (!currentUser) return false;
    const group = groups.find(g => g.id === groupId);
    return group ? group.members.includes(currentUser.uid) : false;
  }, [currentUser, groups]);

  // Check if user is the creator of a group
  const isUserCreator = useCallback((groupId) => {
    if (!currentUser) return false;
    const group = groups.find(g => g.id === groupId);
    return group ? group.createdBy === currentUser.uid : false;
  }, [currentUser, groups]);

  // Check if user is an admin of a group
  const isUserAdmin = useCallback((groupId) => {
    if (!currentUser) return false;
    const group = groups.find(g => g.id === groupId);
    if (!group) return false;
    
    // Creator is always admin
    if (group.createdBy === currentUser.uid) return true;
    
    // Check member roles
    const userRole = group.memberRoles?.[currentUser.uid];
    return userRole === 'admin' || userRole === 'moderator';
  }, [currentUser, groups]);

  // Get user's role in a group
  const getUserRole = useCallback((groupId) => {
    if (!currentUser) return null;
    const group = groups.find(g => g.id === groupId);
    if (!group) return null;
    
    // Creator is always admin
    if (group.createdBy === currentUser.uid) return 'admin';
    
    // Return member role or default to 'member'
    return group.memberRoles?.[currentUser.uid] || 'member';
  }, [currentUser, groups]);

  // Refresh groups data
  const refreshGroups = useCallback(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Clean up orphaned group references from user profile
  const cleanupOrphanedGroups = useCallback(async () => {
    if (!currentUser) return;

    try {
      // Get current groups from Firestore
      const groupsQuery = query(
        collection(db, 'groups'),
        where('members', 'array-contains', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(groupsQuery);
      const validGroupIds = querySnapshot.docs.map(doc => doc.id);
      
      // Get current user profile groups
      const currentProfileGroups = currentUser.groups || [];
      
      // Find orphaned group IDs (in profile but not in actual groups)
      const orphanedGroupIds = currentProfileGroups.filter(id => !validGroupIds.includes(id));
      
      if (orphanedGroupIds.length > 0) {
        // Update user profile to remove orphaned references
        const userDocRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userDocRef, {
          groups: validGroupIds,
          updatedAt: serverTimestamp()
        });
        
        // Refresh user profile to sync the changes
        await refreshUserProfile();
      }
      
      return { cleaned: orphanedGroupIds.length, validGroups: validGroupIds.length };
    } catch (error) {
      console.error('GroupContext: Error cleaning up orphaned groups:', error);
      throw error;
    }
  }, [currentUser, refreshUserProfile]);

  // Cancel an invitation
  const cancelInvitation = useCallback(async (invitationId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to cancel invitations');
    }

    try {
      // Find the invitation in the group's pendingInvites instead of reading the invitation document
      // This avoids permission issues since we're working with data we already have access to
      let invitation = null;
      let group = null;
      
      // Find the group and invitation from our local state
      for (const g of groups) {
        const pendingInvites = g.pendingInvites || [];
        const foundInvitation = pendingInvites.find(inv => inv.id === invitationId);
        if (foundInvitation) {
          invitation = foundInvitation;
          group = g;
          break;
        }
      }
      
      if (!invitation || !group) {
        throw new Error('Invitation not found in group data');
      }
      
      if (!isUserAdmin(group.id) && !isUserCreator(group.id)) {
        throw new Error('Only admins can cancel invitations');
      }
      
      // Remove from group's pending invitations
      const groupRef = doc(db, 'groups', group.id);
      const pendingInvites = group.pendingInvites || [];
      const updatedPendingInvites = pendingInvites.filter(inv => inv.id !== invitationId);
      
      await updateDoc(groupRef, {
        pendingInvites: updatedPendingInvites,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setGroups(prev => prev.map(g => {
        if (g.id === group.id) {
          const pendingInvites = g.pendingInvites || [];
          return {
            ...g,
            pendingInvites: pendingInvites.filter(inv => inv.id !== invitationId),
            updatedAt: new Date()
          };
        }
        return g;
      }));
      
      return { success: true, invitationId };
    } catch (error) {
      console.error('GroupContext: Error cancelling invitation:', error);
      throw error;
    }
  }, [currentUser, groups, isUserAdmin, isUserCreator]);

  // Resend an invitation
  const resendInvitation = useCallback(async (invitationId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to resend invitations');
    }

    try {
      // Get the invitation to check permissions
      const invitationRef = doc(db, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      const invitation = invitationDoc.data();
      
      // Check if user has permission to resend this invitation
      const group = groups.find(g => g.id === invitation.groupId);
      if (!group) {
        throw new Error('Group not found');
      }
      
      if (!isUserAdmin(invitation.groupId) && !isUserCreator(invitation.groupId)) {
        throw new Error('Only admins can resend invitations');
      }
      
      // Update invitation with new timestamp
      await updateDoc(invitationRef, {
        invitedAt: serverTimestamp(),
        resentAt: serverTimestamp(),
        resentBy: currentUser.uid,
        resendCount: (invitation.resendCount || 0) + 1
      });
      
      // Update group's pending invitations timestamp
      const groupRef = doc(db, 'groups', invitation.groupId);
      const pendingInvites = group.pendingInvites || [];
      const updatedPendingInvites = pendingInvites.map(inv => {
        if (inv.id === invitationId) {
          return {
            ...inv,
            invitedAt: serverTimestamp(),
            resentAt: serverTimestamp()
          };
        }
        return inv;
      });
      
      await updateDoc(groupRef, {
        pendingInvites: updatedPendingInvites,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setGroups(prev => prev.map(g => {
        if (g.id === invitation.groupId) {
          const pendingInvites = g.pendingInvites || [];
          return {
            ...g,
            pendingInvites: pendingInvites.map(inv => {
              if (inv.id === invitationId) {
                return {
                  ...inv,
                  invitedAt: serverTimestamp(),
                  resentAt: serverTimestamp()
                };
              }
              return inv;
            }),
            updatedAt: new Date()
          };
        }
        return g;
      }));
      
      return { success: true, invitationId };
    } catch (error) {
      console.error('GroupContext: Error resending invitation:', error);
      throw error;
    }
  }, [currentUser, groups, isUserAdmin, isUserCreator]);



  // Load groups when user changes
  useEffect(() => {
    if (currentUser) {
      fetchGroups();
      // Clean up orphaned groups on initial load (only once)
      if (groups.length === 0) {
        cleanupOrphanedGroups();
      }
    } else {
      setGroups([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, fetchGroups, cleanupOrphanedGroups, groups.length]);

  const value = {
    groups,
    loading,
    error,
    createGroup,
    updateGroup,
    deleteGroup,
    addMemberToGroup,
    removeMemberFromGroup,
    updateMemberRole,
    sendInvitation,
    getGroupById,
    isUserMember,
    isUserCreator,
    isUserAdmin,
    getUserRole,
    refreshGroups,
    cleanupOrphanedGroups,
    fetchUserInvitations,
    acceptInvitation,
    declineInvitation,
    leaveGroup,
    cancelInvitation,
    resendInvitation
  };

  return (
    <GroupContext.Provider value={value}>
      {children}
    </GroupContext.Provider>
  );
}
